import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, Clock, ShieldCheck, ArrowRight, RotateCcw, AlertTriangle } from 'lucide-react';
import { orderUtils } from '../../../utils/orderUtils';

/**
 * PaymentStatusSheet
 * Premium, modern bottom sheet for payment verification and order confirmation lifecycle.
 *
 * States:
 *  - 'verifying' : Animated 3-step live verification sequence (3.5s)
 *  - 'success'   : Celebratory checkmark, Order #, Amount Paid, 5s countdown & Track Order button
 *  - 'failed'    : Red alert with realistic banking refund guidance & instant retry logic
 *  - 'delayed'   : Processing delay status with direct order tracking CTA
 */
const PaymentStatusSheet = ({
  isOpen,
  state,
  orderData,
  errorMessage,
  theme,
  wasPaidFlow,
  onRetry,
  onClose,
  onViewOrderDetails,
}) => {
  const accent = theme?.secondary || '#000000';
  const textOnAccent = theme?.primary || '#ffffff';

  // Live verification steps progress (0, 1, 2)
  const [verifyStep, setVerifyStep] = useState(0);

  // 5-second countdown timer for success auto-redirect
  const [countdown, setCountdown] = useState(5);

  // Reset/Trigger verification step sequence when entering 'verifying'
  useEffect(() => {
    if (state === 'verifying') {
      setVerifyStep(0);
      const timer1 = setTimeout(() => setVerifyStep(1), 1200);
      const timer2 = setTimeout(() => setVerifyStep(2), 2400);
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [state]);

  // Countdown timer when state is 'success'
  useEffect(() => {
    if (state === 'success' && isOpen) {
      setCountdown(5);
      const interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            onViewOrderDetails?.();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [state, isOpen, onViewOrderDetails]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const targetOrderId = orderData?.id || orderData?.orderId || orderData?._id;
  const shortOrderCode = orderData?.orderNumber
    ? orderUtils.getShortCode(orderData.orderNumber)
    : targetOrderId ? targetOrderId.slice(-6).toUpperCase() : '---';

  const amountDisplay = orderData?.finalAmount != null
    ? (orderData.finalAmount / 100).toFixed(2)
    : null;

  return (
    <AnimatePresence>
      {isOpen && state && (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center p-0 sm:p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            onClick={state === 'verifying' ? undefined : onClose}
          />

          {/* Sheet Modal Container */}
          <motion.div
            initial={{ y: '100%', opacity: 0.5 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 28, stiffness: 320 }}
            className="relative w-full max-w-md bg-white rounded-t-[32px] sm:rounded-[32px] p-6 pb-8 shadow-2xl z-10 max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Grab Handle */}
            <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto mb-5 sm:hidden" />

            {/* ─── State 1: Verifying ────────────────────────────────────────── */}
            {state === 'verifying' && (
              <div className="flex flex-col items-center text-center py-3">
                {/* Glowing Spinner Centerpiece */}
                <div className="relative w-20 h-20 mb-5 flex items-center justify-center">
                  <div
                    className="absolute inset-0 rounded-full border-[3.5px] border-t-transparent animate-spin"
                    style={{ borderColor: `${accent}25`, borderTopColor: accent }}
                  />
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center shadow-lg"
                    style={{ backgroundColor: accent, color: textOnAccent }}
                  >
                    <ShieldCheck className="w-6 h-6 animate-pulse" />
                  </div>
                </div>

                <h3 className="text-xl font-black text-gray-900 mb-1">
                  Verifying Payment...
                </h3>
                <p className="text-xs text-gray-500 max-w-xs mb-6">
                  Please hold on while we confirm your payment with the bank.
                </p>

                {/* 3-Step Live Checklist */}
                <div className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 mb-5 space-y-3 text-left">
                  {/* Step 1 */}
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-xs font-semibold text-gray-800">
                      Payment Authorized by Gateway
                    </span>
                  </div>

                  {/* Step 2 */}
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                      verifyStep >= 1 ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600 animate-spin'
                    }`}>
                      {verifyStep >= 1 ? (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      ) : (
                        <Clock className="w-3.5 h-3.5" />
                      )}
                    </div>
                    <span className={`text-xs font-semibold transition-colors ${
                      verifyStep >= 1 ? 'text-gray-800' : 'text-amber-700'
                    }`}>
                      Cryptographic Bank Verification
                    </span>
                  </div>

                  {/* Step 3 */}
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                      verifyStep >= 2 ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-200 text-gray-400'
                    }`}>
                      {verifyStep >= 2 ? (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      ) : (
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                      )}
                    </div>
                    <span className={`text-xs font-semibold transition-colors ${
                      verifyStep >= 2 ? 'text-gray-800' : 'text-gray-400'
                    }`}>
                      Connecting to Kitchen Display
                    </span>
                  </div>
                </div>

                <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100/80 rounded-full text-[11px] text-gray-500 font-medium">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                  <span>256-Bit SSL Encrypted Transaction</span>
                </div>
              </div>
            )}

            {/* ─── State 2: Success ──────────────────────────────────────────── */}
            {state === 'success' && (
              <div className="flex flex-col items-center text-center py-2">
                {/* Bouncy Checkmark */}
                <motion.div
                  initial={{ scale: 0, rotate: -45 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', damping: 15, stiffness: 300 }}
                  className="w-20 h-20 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mb-3 ring-8 ring-emerald-50/60"
                >
                  <CheckCircle2 className="w-11 h-11" strokeWidth={2.5} />
                </motion.div>

                <h3 className="text-2xl font-black text-gray-900 mb-1">
                  {wasPaidFlow ? 'Payment Successful!' : 'Order Placed!'}
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  {wasPaidFlow 
                    ? 'Your payment was received. Waiting for restaurant approval.' 
                    : 'Your order was submitted. Waiting for restaurant to accept.'}
                </p>

                {/* Awaiting Approval Live Status Pill */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200/80 text-amber-800 text-xs font-bold mb-5 shadow-xs">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                  </span>
                  <span>Awaiting Restaurant Approval</span>
                </div>

                {/* Order Summary Card */}
                <div className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 mb-5 text-left flex items-center justify-between shadow-inner">
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                      Order Code
                    </span>
                    <p className="text-lg font-black text-gray-900 tracking-wide mt-0.5">
                      #{shortOrderCode}
                    </p>
                  </div>
                  {wasPaidFlow && amountDisplay && (
                    <div className="text-right">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                        Paid Amount
                      </span>
                      <p className="text-lg font-black text-emerald-600 mt-0.5">
                        ₹{amountDisplay}
                      </p>
                    </div>
                  )}
                </div>

                {/* Countdown Progress */}
                <div className="w-full mb-3 flex items-center justify-between text-xs text-gray-400 font-medium px-1">
                  <span>Auto-tracking in <strong>{countdown}s</strong></span>
                  <div className="w-28 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-emerald-500"
                      initial={{ width: '100%' }}
                      animate={{ width: `${(countdown / 5) * 100}%` }}
                      transition={{ ease: 'linear', duration: 1 }}
                    />
                  </div>
                </div>

                {/* Primary CTA */}
                <button
                  onClick={onViewOrderDetails}
                  className="w-full py-4 rounded-2xl font-bold text-sm shadow-lg transition-all active:scale-[0.98] flex items-center justify-center gap-2 cursor-pointer"
                  style={{ backgroundColor: accent, color: textOnAccent }}
                >
                  <span>Track Live Order Now</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* ─── State 3: Failed ───────────────────────────────────────────── */}
            {state === 'failed' && (
              <div className="flex flex-col items-center text-center py-2">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', damping: 15, stiffness: 300 }}
                  className="w-20 h-20 bg-rose-50 text-rose-600 rounded-full flex items-center justify-center mb-3 ring-8 ring-rose-50/60"
                >
                  <XCircle className="w-11 h-11" strokeWidth={2.5} />
                </motion.div>

                <h3 className="text-2xl font-black text-gray-900 mb-1">
                  Payment Failed
                </h3>
                <p className="text-xs text-rose-600 font-medium mb-4 max-w-xs leading-relaxed">
                  {errorMessage || 'The payment could not be processed.'}
                </p>

                {/* Banking Refund Assurance */}
                <div className="w-full bg-amber-50/90 border border-amber-200/80 rounded-2xl p-4 mb-5 text-left flex items-start gap-2.5">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-900 leading-relaxed">
                    <p className="font-bold mb-0.5">Was money debited from your bank?</p>
                    <p className="text-amber-800">
                      If debited, your bank will automatically reverse and refund the full amount within <strong>5–7 business days</strong>.
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="w-full flex flex-col gap-2.5">
                  {onRetry && (
                    <button
                      onClick={onRetry}
                      className="w-full py-3.5 rounded-2xl font-bold text-sm shadow-md transition-all active:scale-[0.98] flex items-center justify-center gap-2 cursor-pointer"
                      style={{ backgroundColor: accent, color: textOnAccent }}
                    >
                      <RotateCcw className="w-4 h-4" />
                      <span>Retry Payment</span>
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="w-full py-3 rounded-2xl font-bold text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all active:scale-[0.98] cursor-pointer"
                  >
                    Change Payment Method
                  </button>
                </div>
              </div>
            )}

            {/* ─── State 4: Delayed ──────────────────────────────────────────── */}
            {state === 'delayed' && (
              <div className="flex flex-col items-center text-center py-2">
                <div className="w-20 h-20 bg-amber-50 text-amber-600 rounded-full flex items-center justify-center mb-3 ring-8 ring-amber-50/60">
                  <Clock className="w-10 h-10" />
                </div>

                <h3 className="text-2xl font-black text-gray-900 mb-1">
                  Payment Under Verification
                </h3>
                <p className="text-xs text-gray-600 mb-4 max-w-xs">
                  We are waiting for final confirmation from your bank or payment provider.
                </p>

                <div className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 mb-5 text-left text-xs text-gray-600 leading-relaxed">
                  <p className="font-bold text-gray-800 mb-1">Next Steps:</p>
                  <ul className="list-disc pl-4 space-y-1">
                    <li>If the payment went through on your UPI/bank app, your order will confirm automatically.</li>
                    <li>You can track the live status anytime in your Order Tracking page.</li>
                  </ul>
                </div>

                <div className="w-full flex flex-col gap-2.5">
                  <button
                    onClick={onViewOrderDetails}
                    className="w-full py-3.5 rounded-2xl font-bold text-sm shadow-md transition-all active:scale-[0.98] flex items-center justify-center gap-2 cursor-pointer"
                    style={{ backgroundColor: accent, color: textOnAccent }}
                  >
                    <span>Check Order Status</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={onClose}
                    className="w-full py-3 rounded-2xl font-bold text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all active:scale-[0.98] cursor-pointer"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PaymentStatusSheet;
